import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from './../api.js';
import { CURRENT_SEASON } from '../../core/current-season.js';

Alpine.data('playersView', () => ({

  loading: true,
  firstLoad: true,
  error: '',
  player: { dartSetups: [], role: '', stats: {} },
  trainingProgress: null,
  trainingProgressLoading: false,
  matchHistory: [],
  matchHistoryLoading: false,

  mode: 'season', // 'season' | 'career' | 'league'
  seasons: { available: [], current: null, prev: null, next: null },
  seasonStats: null,
  leagueStats: null,

  readRouteId() {
    const parts = location.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('players');
    return (idx >= 0 && parts[idx + 1]) ? decodeURIComponent(parts[idx + 1]) : null;
  },

  readQuerySeason() {
    const u = new URL(location.href);
    const s = u.searchParams.get('season');
    return s && /^\d{4}$/.test(s) ? s : null;
  },

  isSelfProfileRequest(id) {
    const want = String(id || '').trim().toLowerCase();
    if (!want) return false;
    const S = Alpine.store('session');
    const me = S?.me || {};
    const candidates = [
      S?.rosterSelfPlayerId,
      me?.playerId,
      me?._id,
      me?.email,
    ].map((v) => String(v || '').trim().toLowerCase()).filter(Boolean);
    return candidates.includes(want);
  },

  isStaffAccount() {
    const role = String(Alpine.store('session')?.me?.role || '').toLowerCase();
    return !!role && role !== 'player';
  },

  editUrl() {
    if (!this.player?._id) return '/admin/players';
    return `/admin/players/${encodeURIComponent(this.player._id)}/edit`;
  },

  teamUrl(team) {
    if (!team?._id) return '/teams';
    return `/admin/teams/${encodeURIComponent(team._id)}`;
  },

  venueLabel(team) {
    const venue = team?.venue;
    if (!venue?.name) return 'No venue assigned';
    return venue.address ? `${venue.name} · ${venue.address}` : venue.name;
  },

  captainLabel(team) {
    const captain = team?.captainPlayerId;
    if (captain?.fullName) {
      return captain.nickname ? `${captain.fullName} (${captain.nickname})` : captain.fullName;
    }
    return team?.captainName || '—';
  },

  get primarySetup() {
    return Array.isArray(this.player.dartSetups)
      ? this.player.dartSetups.find(s => s.isPrimary) || this.player.dartSetups[0] || null
      : null;
  },

  get otherSetups() {
    if (!Array.isArray(this.player.dartSetups)) return [];
    const primId = this.primarySetup?._id?.toString();
    return this.player.dartSetups.filter(s => s._id?.toString() !== primId);
  },

  get statsView() {
    if (this.mode === 'career') return this.player?.stats || {};
    if (this.mode === 'league') return this.leagueStats || this.seasonStats || {};
    return this.seasonStats || {};
  },

  get highestCheckoutCard() {
    const stats = this.statsView || {};
    const detail = stats?.highestCheckoutDetail || {};
    return {
      total: Number(stats?.highestCheckout || 0),
      gameLabel: detail?.gameLabel || '—',
      section: detail?.section || '',
      close: detail?.close || '',
      round: detail?.round || '',
      date: detail?.date || '',
    };
  },

  fmtPart(p) {
    if (!p) return '—';
    const bits = [];
    if (p.brand) bits.push(p.brand);
    if (p.model) bits.push(p.model);
    if (p.tungstenPercent != null) bits.push(`${p.tungstenPercent}% W`);
    if (p.weight != null) bits.push(`${p.weight}${p.weightUnit || 'g'}`);
    if (p.length != null) bits.push(`${p.length}${p.lengthUnit || 'mm'}`);
    if (p.maxDiameter != null) bits.push(`Ø${p.maxDiameter}`);
    if (p.material) bits.push(p.material);
    if (p.shape) bits.push(p.shape);
    if (p.texture) bits.push(p.texture);
    if (p.lengthLabel) bits.push(p.lengthLabel);
    if (p.thread) bits.push(p.thread);
    return bits.length ? bits.join(' · ') : '—';
  },

  fmtWeight(val, unit) {
    if (val == null || Number.isNaN(Number(val))) return '';
    return `${val}${unit || 'g'}`;
  },

  fmtBool(v) {
    return v ? 'Enabled' : 'Disabled';
  },

  winRateLabel(won, played) {
    const p = Number(played) || 0;
    if (!p) return '—';
    const w = Number(won) || 0;
    return `${Math.round((w / p) * 100)}%`;
  },

  fmtDate(d) {
    if (!d) return '—';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString('en-GB');
  },

  zoom: { open: false, src: '' },
  openZoom(src) { this.zoom.src = src || '/assets/placeholder-darts-setup.png'; this.zoom.open = true; },
  closeZoom() { this.zoom.open = false; this.zoom.src = ''; },
  scrollToTopAfterPageChange() {
    if (window.innerWidth >= 768) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },

  normalizeSeasonSelection(requestedSeason) {
    const request = String(requestedSeason || '').trim();
    if (!request || !Array.isArray(this.seasons?.available)) return;
    const match = this.seasons.available.find((s) => String(s) === request);
    if (match != null) this.seasons.current = match;
  },

  async loadPlayer(season, scope) {
    const id = this.readRouteId();
    if (!id) throw new Error('Missing player id');
    const qs = new URLSearchParams();
    const s = season || this.seasons?.current || CURRENT_SEASON;
    if (s) qs.set('season', String(s));
    const sc = scope || (this.mode === 'career' ? 'career' : this.mode === 'league' ? 'league' : 'season');
    qs.set('scope', sc);
    const res = await apiFetch(`/players/${id}?${qs.toString()}`);
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      const msg = String(e.message || 'Failed to load player');
      if (msg === 'player_not_found' && this.isSelfProfileRequest(id) && this.isStaffAccount()) {
        globalThis.location.replace('/admin/account-settings#profile');
        return;
      }
      throw new Error(msg === 'player_not_found'
        ? (this.isStaffAccount()
          ? 'Player not found. Staff accounts without a roster link can manage their profile under Account settings.'
          : 'Your roster profile could not be loaded. Try again or contact your league admin.')
        : msg);
    }
    const data = await res.json();

    this.player = data.player || {};
    this.seasons = data.seasons || { available: [], current: null, prev: null, next: null };
    const stats = data.player?.stats || data.stats || {};
    if (sc === 'career') {
      this.player.stats = stats;
      this.seasonStats = null;
      this.leagueStats = null;
    } else if (sc === 'league') {
      this.leagueStats = stats;
      this.seasonStats = data.seasonStats || stats;
    } else {
      this.seasonStats = data.seasonStats || stats;
      this.leagueStats = null;
    }
    this.normalizeSeasonSelection(season);

    if (sc === 'season' && !this.seasonStats) this.mode = 'career';
  },

  badgeTierClass(tier) {
    const k = String(tier || '').toLowerCase();
    if (k === 'gold') return 'border-amber-300/40 bg-amber-500/20 text-amber-100';
    if (k === 'silver') return 'border-slate-300/40 bg-slate-400/20 text-slate-100';
    if (k === 'bronze') return 'border-orange-300/40 bg-orange-500/20 text-orange-100';
    return 'border-white/10 bg-white/5 text-neutral-300';
  },

  async loadTrainingProgress() {
    if (!this.player?._id) return;
    this.trainingProgressLoading = true;
    try {
      const res = await apiFetch(`/training/progress/${encodeURIComponent(this.player._id)}`);
      const data = await res.json().catch(() => ({}));
      this.trainingProgress = res.ok ? data : null;
    } catch {
      this.trainingProgress = null;
    } finally {
      this.trainingProgressLoading = false;
    }
  },

  matchSourceLabel(row) {
    const t = String(row?.sourceType || 'match').replace(/_/g, ' ');
    return t.charAt(0).toUpperCase() + t.slice(1);
  },

  matchResultClass(row) {
    const r = String(row?.result || '').toLowerCase();
    if (r === 'won') return 'text-emerald-300';
    if (r === 'lost') return 'text-rose-300';
    return 'text-neutral-300';
  },

  async loadMatchHistory() {
    if (!this.player?._id) return;
    this.matchHistoryLoading = true;
    try {
      const qs = new URLSearchParams({ limit: '12' });
      const season = this.seasons?.current || CURRENT_SEASON;
      if (season) qs.set('season', String(season));
      const res = await apiFetch(`/players/${encodeURIComponent(this.player._id)}/match-history?${qs.toString()}`);
      const data = await res.json().catch(() => ({}));
      this.matchHistory = res.ok && Array.isArray(data.items) ? data.items : [];
    } catch {
      this.matchHistory = [];
    } finally {
      this.matchHistoryLoading = false;
    }
  },

  async setMode(m) {
    this.mode = m;
    this.loading = true;
    try {
      await this.loadPlayer(this.seasons?.current, m);
      await this.loadMatchHistory();
    } finally {
      this.loading = false;
    }
  },
  async jumpToSeason(s) {
    const targetSeason = String(s || '').trim();
    if (!targetSeason) return;
    this.loading = true;
    try {
      await this.loadPlayer(targetSeason);
      await this.loadTrainingProgress();
      await this.loadMatchHistory();
      this.mode = 'season';
      this.pushSeasonToUrl(targetSeason);
      this.scrollToTopAfterPageChange();
    } finally {
      this.loading = false;
    }
  },

  // NEW: keep ?season in the URL for sharing
  pushSeasonToUrl(s) {
    const u = new URL(location.href);
    if (s) u.searchParams.set('season', s); else u.searchParams.delete('season');
    history.replaceState(null, '', u.toString());
  },


  async init() {
    try {
      const qSeason = this.readQuerySeason();
      await this.loadPlayer(qSeason || CURRENT_SEASON);
      await this.loadTrainingProgress();
      await this.loadMatchHistory();
      this.mode = 'season';
      this.pushSeasonToUrl(this.seasons.current); // normalize
    } catch (e) {
      this.error = e.message || 'Failed to load player';
    } finally {
      this.loading = false;
      this.firstLoad = false;
    }
  }
}));
